import {
  Chart,
  ChartFooterChard,
  Typography,
  colors,
  IconGraphy,
  Avatar,
  styles,
} from '@leapeasy/ui-kit';
import { Box } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as DashboardEmptyStateIcon } from 'assets/dashboard-empty-state.svg';
import { toChartNumber } from 'utils/chartMetrics';
import { ALL_APPLICATIONS_CHART_LABELS } from 'utils/residentTerminology';

export const AllApplicationsChart = ({ data = {}, onClickItem }) => {
  const stage00 = toChartNumber(data.stage00);
  const stage04 = toChartNumber(data.stage04);
  const stage05 = toChartNumber(data.stage05);
  const stage06 = toChartNumber(data.stage06);
  const cancelledCount = toChartNumber(data.cancelledCount);
  const pendingAppsStage0102A =
    toChartNumber(data.stage01) + toChartNumber(data.stage02) + toChartNumber(data.stage02A);

  const sum = stage00 + pendingAppsStage0102A + stage04 + stage05 + stage06 + cancelledCount;
  const toPercent = (value) => (sum > 0 ? (value / sum) * 100 : 0);

  if (sum <= 0) {
    return (
      <EmptyStateContainer
        boxShadow={styles.boxShadow.sm}
        borderRadius={`${styles.borderRadius.large}px`}
        border={`1px solid ${colors.black[300]}`}
      >
        <Box display="flex" gap="16px" alignItems="center">
          <Avatar
            size="large"
            iconImage={
              <IconGraphy icon="FileFolder.TextSnippet" style={{ width: '24px', height: '24px' }} />
            }
            bgColor={colors.purple[25]}
            color={colors.purple[600]}
          />
          <Box>
            <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
              All Applications
            </Typography>
            <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
              Here is an overview of the current status of all applications.
            </Typography>
          </Box>
        </Box>

        <EmptyBody>
          <DashboardEmptyStateIcon />
          <Typography
            variant="body2"
            style={{
              textAlign: 'center',
              color: colors.neutral[900],
              maxWidth: '360px',
              fontWeight: '500',
            }}
          >
            There&apos;s not any records in this table and no more information to show.
          </Typography>
        </EmptyBody>
      </EmptyStateContainer>
    );
  }

  return (
    <Chart
      variant="pie"
      pieMinAngle={30}
      data={[
        {
          name: ALL_APPLICATIONS_CHART_LABELS.potentialApps,
          value: toPercent(stage00),
        },
        {
          name: ALL_APPLICATIONS_CHART_LABELS.pendingApps,
          value: toPercent(pendingAppsStage0102A),
        },
        {
          name: ALL_APPLICATIONS_CHART_LABELS.pendingPayment,
          value: toPercent(stage04),
        },
        {
          name: ALL_APPLICATIONS_CHART_LABELS.policyIssued,
          value: toPercent(stage05),
        },
        {
          name: ALL_APPLICATIONS_CHART_LABELS.declinedApplication,
          value: toPercent(stage06),
        },
        {
          name: ALL_APPLICATIONS_CHART_LABELS.cancelledApplication,
          value: toPercent(cancelledCount),
        },
      ]}
      colors={[
        colors.parisGreen[100],
        colors.purpleIrish[100],
        colors.warmBlue[100],
        colors.magentaRed[100],
        colors.sunglow[100],
        colors.tomato[100],
      ]}
      breakpoints={{
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      }}
      title="All Applications"
      description="Here is an overview of the current status of all applications."
      icon={<IconGraphy icon="FileFolder.TextSnippet" />}
      footer={
        <div style={{ gap: '16px', width: '100%', display: 'flex' }}>
          <div
            style={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <ChartFooterChard
              backgroundColor="#ECF9F4"
              activeColor={colors.parisGreen[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {stage00.toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.parisGreen[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    {ALL_APPLICATIONS_CHART_LABELS.potentialApps}
                  </Typography>
                </div>
              }
              onClick={() => onClickItem('00 - Entrata')}
            />

            <ChartFooterChard
              backgroundColor="#ECECFD"
              activeColor={'#4840E9'}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {stage04.toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: '#4840E9',
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    {ALL_APPLICATIONS_CHART_LABELS.pendingPayment}
                  </Typography>
                </div>
              }
              onClick={() => onClickItem('04 - Agreement Signed')}
            />

            <ChartFooterChard
              backgroundColor="#FFF6E5"
              activeColor="#FFC452"
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {stage06.toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: '#DEAE50',
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    {ALL_APPLICATIONS_CHART_LABELS.declinedApplication}
                  </Typography>
                </div>
              }
              onClick={() => onClickItem('06 - Policy Declined')}
            />
          </div>

          <div
            style={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <ChartFooterChard
              backgroundColor={colors.purpleIrish[25]}
              activeColor={colors.purpleIrish[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {pendingAppsStage0102A.toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.purpleIrish[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    {ALL_APPLICATIONS_CHART_LABELS.pendingApps}
                  </Typography>
                </div>
              }
              onClick={() => onClickItem('Pending Apps Stages 01-02A')}
            />

            <ChartFooterChard
              backgroundColor="#FCF0F8"
              activeColor={'#E66FB7'}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {stage05.toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: '#E66FB7',
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    {ALL_APPLICATIONS_CHART_LABELS.policyIssued}
                  </Typography>
                </div>
              }
              onClick={() => onClickItem('05 - Policy Issued')}
            />

            <ChartFooterChard
              backgroundColor="#FEF0ED"
              activeColor="#F66B4C"
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {cancelledCount.toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: '#F66B4C',
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    {ALL_APPLICATIONS_CHART_LABELS.cancelledApplication}
                  </Typography>
                </div>
              }
              onClick={() => onClickItem('Cancelled Application')}
            />
          </div>
        </div>
      }
    />
  );
};

const EmptyStateContainer = styled(Box)`
  width: 100%;
  min-height: 534px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: ${(props) => props.boxShadow};
  border-radius: ${(props) => props.borderRadius};
  border: ${(props) => props.border};
`;

const EmptyBody = styled(Box)`
  width: 100%;
  flex: 1;
  min-height: 442px;
  background: white;
  border: 1px solid ${colors.black[200]};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  box-sizing: border-box;
`;
